import { type Bounds, type Line, type Ray, type Segment } from '@/domain/geometry';
import { EPSILON, type Vec2 } from '@/domain/math';

/**
 * Clipping unbounded geometry to the viewport.
 *
 * An infinite line cannot be drawn; it has to be turned into a segment spanning the
 * visible rectangle. The naive workaround — extending the line by some large constant —
 * fails in two ways that both show up on screen: at high zoom the constant is not large
 * enough and the line ends mid-viewport, and at low zoom the coordinates grow large
 * enough to lose precision and the line visibly wobbles.
 *
 * Liang–Barsky is used rather than Cohen–Sutherland: it is a single pass with no
 * iteration, and it works directly on the parametric form the geometry layer already
 * uses.
 */

/** The visible portion of an infinite line, or `null` if it misses the viewport. */
export function clipLineToBounds(line: Line, bounds: Bounds): Segment | null {
  return clipParametric(line.origin, line.direction, bounds, -Infinity, Infinity);
}

/** The visible portion of a ray, or `null` if it misses the viewport. */
export function clipRayToBounds(ray: Ray, bounds: Bounds): Segment | null {
  return clipParametric(ray.origin, ray.direction, bounds, 0, Infinity);
}

function clipParametric(
  origin: Vec2,
  direction: Vec2,
  bounds: Bounds,
  minT: number,
  maxT: number
): Segment | null {
  let tMin = minT;
  let tMax = maxT;

  // Four half-plane tests: left, right, bottom, top.
  const checks: readonly (readonly [number, number])[] = [
    [-direction.x, origin.x - bounds.minX],
    [direction.x, bounds.maxX - origin.x],
    [-direction.y, origin.y - bounds.minY],
    [direction.y, bounds.maxY - origin.y],
  ];

  for (const check of checks) {
    const p = check[0];
    const q = check[1];

    if (Math.abs(p) <= EPSILON) {
      // Parallel to this edge: reject only if it starts outside.
      if (q < 0) return null;
      continue;
    }

    const t = q / p;
    if (p < 0) {
      if (t > tMax) return null;
      if (t > tMin) tMin = t;
    } else {
      if (t < tMin) return null;
      if (t < tMax) tMax = t;
    }
  }

  if (!Number.isFinite(tMin) || !Number.isFinite(tMax) || tMax <= tMin) return null;

  return {
    a: { x: origin.x + direction.x * tMin, y: origin.y + direction.y * tMin },
    b: { x: origin.x + direction.x * tMax, y: origin.y + direction.y * tMax },
  };
}
